import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { reportsApi } from '@/lib/api';
import api from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import { Users, Building2, AlertCircle, UserPlus, TrendingUp, Clock, FileWarning, Search, CheckCircle, ArrowRight, RefreshCw, History } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend } from 'recharts';
import { motion } from 'framer-motion';

const COLORS = {
  homeowners: '#22c55e',
  renters: '#3b82f6',
  pending: '#eab308',
};

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [tenureData, setTenureData] = useState(null);
  const [statesData, setStatesData] = useState([]);
  const [activeJob, setActiveJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, tenureRes, statesRes, jobRes] = await Promise.all([
        reportsApi.getDashboardStats(),
        reportsApi.getTenure(),
        reportsApi.getStatesDistribution(),
        api.get('/employees/verify-owners/active'),
      ]);
      setStats(statsRes.data);
      setTenureData(tenureRes.data);
      setStatesData(statesRes.data);
      setActiveJob(jobRes.data.status !== 'none' ? jobRes.data : null);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  const pieData = tenureData
    ? [
        { name: 'Homeowners', value: tenureData.homeowners.count, color: COLORS.homeowners },
        { name: 'Renters', value: tenureData.renters.count, color: COLORS.renters },
        { name: 'Pending', value: tenureData.pending.count, color: COLORS.pending },
      ].filter((d) => d.value > 0)
    : [];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-8"
      data-testid="dashboard-page"
    >
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground mt-1">
          Overview of your employee census data
        </p>
      </div>

      {/* Needs Attention */}
      {(stats?.pending_rep_uploads > 0 || stats?.never_looked_up > 0 || activeJob) && (
        <motion.div variants={itemVariants}>
          <Card className="bg-card border-border border-l-4 border-l-[#E31837]" data-testid="needs-attention-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-[#E31837]" />
                Needs Attention
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {stats?.pending_rep_uploads > 0 && (
                <button
                  onClick={() => navigate('/census-upload')}
                  className="w-full flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors text-left"
                  data-testid="attention-pending-uploads"
                >
                  <div className="flex items-center gap-3">
                    <FileWarning className="w-5 h-5 text-yellow-500" />
                    <div>
                      <p className="text-sm font-medium">{stats.pending_rep_uploads} file(s) uploaded by company reps awaiting processing</p>
                      <p className="text-xs text-muted-foreground">Process them through the census wizard</p>
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-muted-foreground" />
                </button>
              )}
              {activeJob && (
                <button
                  onClick={() => navigate('/owner-verification')}
                  className="w-full flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors text-left"
                  data-testid="attention-active-job"
                >
                  <div className="flex items-center gap-3">
                    {activeJob.status === 'running' ? (
                      <RefreshCw className="w-5 h-5 text-blue-500 animate-spin" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-yellow-500" />
                    )}
                    <div>
                      <p className="text-sm font-medium">
                        {activeJob.status === 'running'
                          ? `Owner verification running: ${activeJob.completed} of ${activeJob.total} looked up`
                          : `Owner verification interrupted: ${activeJob.remaining} employees remaining`}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {activeJob.status === 'running' ? 'View live progress' : 'Resume from where it left off'}
                      </p>
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-muted-foreground" />
                </button>
              )}
              {stats?.never_looked_up > 0 && !activeJob && (
                <button
                  onClick={() => navigate('/owner-verification')}
                  className="w-full flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors text-left"
                  data-testid="attention-never-looked-up"
                >
                  <div className="flex items-center gap-3">
                    <Search className="w-5 h-5 text-red-500" />
                    <div>
                      <p className="text-sm font-medium">{stats.never_looked_up} employee(s) never looked up on RPR</p>
                      <p className="text-xs text-muted-foreground">Run owner verification to find homeowners</p>
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-muted-foreground" />
                </button>
              )}
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Recent Uploads */}
      {stats?.recent_uploads?.length > 0 && (
        <motion.div variants={itemVariants}>
          <Card className="bg-card border-border" data-testid="recent-uploads-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <History className="w-4 h-4 text-muted-foreground" />
                Recent Uploads
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="divide-y divide-border">
                {stats.recent_uploads.map((u) => (
                  <div key={u.id} className="py-2 flex items-center justify-between text-sm">
                    <div className="flex items-center gap-3 min-w-0">
                      {u.status === 'pending' ? (
                        <FileWarning className="w-4 h-4 text-yellow-500 shrink-0" />
                      ) : (
                        <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
                      )}
                      <span className="truncate">{u.file_name || 'Census update'}</span>
                      <span className="text-xs text-muted-foreground shrink-0">by {u.uploaded_by_name || 'Unknown'}</span>
                    </div>
                    <div className="flex items-center gap-3 shrink-0 text-xs text-muted-foreground">
                      {u.status === 'pending' && <span className="px-2 py-0.5 rounded-full bg-yellow-500/15 text-yellow-600 font-medium">awaiting processing</span>}
                      {u.added_count > 0 && <span className="text-green-500">+{u.added_count}</span>}
                      {u.quit_count > 0 && <span className="text-red-500">-{u.quit_count}</span>}
                      <span>{new Date(u.timestamp).toLocaleDateString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Stats Grid */}
      <motion.div
        variants={containerVariants}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
      >
        <motion.div variants={itemVariants}>
          <Card className="stat-card bg-card border-border" data-testid="stat-total-employees">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Employees</p>
                  <p className="text-3xl font-bold mt-1">{stats?.total_employees || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Users className="w-6 h-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div variants={itemVariants}>
          <Card className="stat-card bg-card border-border" data-testid="stat-total-companies">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Companies</p>
                  <p className="text-3xl font-bold mt-1">{stats?.total_companies || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-blue-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div variants={itemVariants}>
          <Card className="stat-card bg-card border-border" data-testid="stat-pending-verification">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Pending Verification</p>
                  <p className="text-3xl font-bold mt-1">{stats?.pending_verification || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-yellow-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div variants={itemVariants}>
          <Card className="stat-card bg-card border-border" data-testid="stat-new-hires">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">New Hires (30d)</p>
                  <p className="text-3xl font-bold mt-1">{stats?.new_hires_30d || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <UserPlus className="w-6 h-6 text-green-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Homeowner Distribution */}
        <motion.div variants={itemVariants}>
          <Card className="bg-card border-border" data-testid="chart-homeowner-distribution">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                Homeowner Distribution
              </CardTitle>
            </CardHeader>
            <CardContent>
              {pieData.length > 0 ? (
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={5}
                        dataKey="value"
                        label={({ name, percent }) =>
                          `${name} ${(percent * 100).toFixed(0)}%`
                        }
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          background: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px',
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                  No employee data available
                </div>
              )}
              <div className="flex justify-center gap-6 mt-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ background: COLORS.homeowners }} />
                  <span className="text-sm text-muted-foreground">Homeowners</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ background: COLORS.renters }} />
                  <span className="text-sm text-muted-foreground">Renters</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ background: COLORS.pending }} />
                  <span className="text-sm text-muted-foreground">Pending</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Tenure Stats */}
        <motion.div variants={itemVariants}>
          <Card className="bg-card border-border" data-testid="chart-tenure-stats">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="w-5 h-5 text-primary" />
                Average Tenure (Days)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {tenureData ? (
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={[
                        {
                          name: 'Homeowners',
                          avg: tenureData.homeowners.avg_tenure_days,
                          count: tenureData.homeowners.count,
                        },
                        {
                          name: 'Renters',
                          avg: tenureData.renters.avg_tenure_days,
                          count: tenureData.renters.count,
                        },
                        {
                          name: 'Pending',
                          avg: tenureData.pending.avg_tenure_days,
                          count: tenureData.pending.count,
                        },
                      ]}
                      layout="vertical"
                      margin={{ top: 20, right: 30, left: 60, bottom: 5 }}
                    >
                      <XAxis type="number" stroke="hsl(var(--muted-foreground))" />
                      <YAxis dataKey="name" type="category" stroke="hsl(var(--muted-foreground))" />
                      <Tooltip
                        contentStyle={{
                          background: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px',
                        }}
                        formatter={(value, name) => [
                          name === 'avg' ? `${value} days` : value,
                          name === 'avg' ? 'Avg Tenure' : 'Count',
                        ]}
                      />
                      <Bar dataKey="avg" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                  No tenure data available
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* States Distribution */}
      {statesData.length > 0 && (
        <motion.div variants={itemVariants}>
          <Card className="bg-card border-border" data-testid="chart-states-distribution">
            <CardHeader>
              <CardTitle className="text-lg">Employee Distribution by State</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={statesData.slice(0, 10)}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <XAxis dataKey="state" stroke="hsl(var(--muted-foreground))" />
                    <YAxis stroke="hsl(var(--muted-foreground))" />
                    <Tooltip
                      contentStyle={{
                        background: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px',
                      }}
                    />
                    <Legend />
                    <Bar dataKey="homeowners" name="Homeowners" fill={COLORS.homeowners} stackId="a" />
                    <Bar dataKey="renters" name="Renters" fill={COLORS.renters} stackId="a" />
                    <Bar dataKey="pending" name="Pending" fill={COLORS.pending} stackId="a" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </motion.div>
  );
}
